<template>
  <div class="landscape-container">
    <!-- 左上角装饰图片 -->
    <div class="top-left-decoration">
      <img src="@/assets/leaderboard/top-left.png" alt="装饰图片" />
    </div>

    <!-- 全屏按钮 -->
    <div class="fullscreen-btn" :class="{ 'fullscreen-mode': isFullscreen }" @click="toggleFullscreen">
      <i :class="isFullscreen ? 'el-icon-copy-document' : 'el-icon-full-screen'"></i>
    </div>

    <!-- Title -->
    <div class="title-section">
      <div class="title-container">
        <h1 class="title-text">战队龙虎榜</h1>
        <span class="sort-indicator">{{ currentSortType === 'amount' ? '下单金额' : '目标达成率' }}</span>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-container">
      <!-- Left: Ranking List (2/10 width) -->
      <div class="ranking-section">
        <RankCard :mode="'landscape'" />
      </div>

      <!-- Right: Grid Blocks (8/10 width) -->
      <div class="grid-section">
        <div class="grid-wrapper" :class="{ 'no-transition': !isAnimating }" :style="{ transform: `translateY(${translateYPercent}%)` }">
          <!-- 使用 v-for 循环生成3组 grid -->
          <template v-for="(group, groupIndex) in gridGroups">
            <div :key="`${groupIndex}-amount`" class="grid-container">
              <div class="grid-item">
                <DashboardCard
                  :title="`周榜业绩团队 ${currentWeekRange.start}-${currentWeekRange.end}`"
                  :headers="weeklyTeamDataByAmount.headers"
                  :rows="weeklyTeamDataByAmount.rows"
                  :mode="'landscape'"
                />
              </div>
              <div class="grid-item">
                <DashboardCard
                  :title="`${currentMonth.format('M')}月榜业绩团队`"
                  :headers="monthlyTeamDataByAmount.headers"
                  :rows="monthlyTeamDataByAmount.rows"
                  :mode="'landscape'"
                />
              </div>
              <div class="grid-item">
                <DashboardCard
                  :title="`周榜业绩个人 ${currentWeekRange.start}-${currentWeekRange.end}`"
                  :headers="weeklyIndividualDataByAmount.headers"
                  :rows="weeklyIndividualDataByAmount.rows"
                  :mode="'landscape'"
                />
              </div>
              <div class="grid-item">
                <DashboardCard
                  :title="`${currentMonth.format('M')}月榜业绩个人`"
                  :headers="monthlyIndividualDataByAmount.headers"
                  :rows="monthlyIndividualDataByAmount.rows"
                  :mode="'landscape'"
                />
              </div>
            </div>
            <div :key="`${groupIndex}-rate`" class="grid-container">
              <div class="grid-item">
                <DashboardCard
                  :title="`周榜业绩团队 ${currentWeekRange.start}-${currentWeekRange.end}`"
                  :headers="weeklyTeamDataByRate.headers"
                  :rows="weeklyTeamDataByRate.rows"
                  :mode="'landscape'"
                />
              </div>
              <div class="grid-item">
                <DashboardCard
                  :title="`${currentMonth.format('M')}月榜业绩团队`"
                  :headers="monthlyTeamDataByRate.headers"
                  :rows="monthlyTeamDataByRate.rows"
                  :mode="'landscape'"
                />
              </div>
              <div class="grid-item">
                <DashboardCard
                  :title="`周榜业绩个人 ${currentWeekRange.start}-${currentWeekRange.end}`"
                  :headers="weeklyIndividualDataByRate.headers"
                  :rows="weeklyIndividualDataByRate.rows"
                  :mode="'landscape'"
                />
              </div>
              <div class="grid-item">
                <DashboardCard
                  :title="`${currentMonth.format('M')}月榜业绩个人`"
                  :headers="monthlyIndividualDataByRate.headers"
                  :rows="monthlyIndividualDataByRate.rows"
                  :mode="'landscape'"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import RankCard from './components/rank.vue'
import DashboardCard from './components/card.vue'

@Component({
  components: {
    RankCard,
    DashboardCard
  }
})
export default class LandscapeComponent extends Vue {
  @Prop({ required: true }) leaderboardData
  @Prop({ required: true }) currentMonth
  @Prop({ required: true }) currentWeekRange
  @Prop({ required: true }) weeklyTeamDataByAmount
  @Prop({ required: true }) monthlyTeamDataByAmount
  @Prop({ required: true }) weeklyIndividualDataByAmount
  @Prop({ required: true }) monthlyIndividualDataByAmount
  @Prop({ required: true }) weeklyTeamDataByRate
  @Prop({ required: true }) monthlyTeamDataByRate
  @Prop({ required: true }) weeklyIndividualDataByRate
  @Prop({ required: true }) monthlyIndividualDataByRate
  @Prop({ required: true }) autoScrollInterval

  currentSortType = 'amount' // 'amount' | 'rate'
  isFullscreen = false
  gridGroups = [0, 1, 2] // 3组循环的grid
  currentPosition = 2 // 当前位置索引 (0-5: 第1组amount, 第1组rate, 第2组amount, 第2组rate, 第3组amount, 第3组rate)
  translateYPercent = -33.33 // 初始位置，显示第2组的amount (第3个grid)
  isAnimating = false
  scrollCount = 0 // 滚动计数器

  // 自动滚动相关配置
  autoScrollTimer = null // 自动滚动定时器
  isAutoScrollPaused = false // 是否暂停自动滚动
  lastManualOperationTime = 0 // 最后一次手动操作的时间戳

  scrollToNext() {
    if (this.isAnimating) return

    this.isAnimating = true
    this.scrollCount++

    // 计算下一个位置
    const nextPosition = (this.currentPosition + 1) % 6
    const nextType = nextPosition % 2 === 0 ? 'amount' : 'rate'

    // 更新排序类型
    this.currentSortType = nextType

    // 向下滚动一个grid
    this.translateYPercent = this.translateYPercent - 16.67

    // 判断是否需要重置位置（滚动了2次，回到相同类型）
    if (this.scrollCount >= 2 && nextType === 'amount') {
      // 动画结束后重置位置
      setTimeout(() => {
        this.isAnimating = false
        this.$nextTick(() => {
          this.translateYPercent = -33.33  // 重置到第2组的amount位置
          this.currentPosition = 2
          this.scrollCount = 0
        })
      }, 600)
    } else {
      // 正常更新位置
      this.currentPosition = nextPosition
      setTimeout(() => {
        this.isAnimating = false
      }, 600)
    }
  }

  // 启动自动滚动
  startAutoScroll() {
    this.stopAutoScroll()
    if (this.autoScrollInterval > 0 && !this.isAutoScrollPaused) {
      this.autoScrollTimer = setInterval(() => {
        this.scrollToNext()
      }, this.autoScrollInterval * 1000)
    }
  }

  // 停止自动滚动
  stopAutoScroll() {
    if (this.autoScrollTimer) {
      clearInterval(this.autoScrollTimer)
      this.autoScrollTimer = null
    }
  }

  // 暂停自动滚动（手动操作后恢复）
  pauseAutoScrollTemporarily() {
    this.lastManualOperationTime = Date.now()
    this.stopAutoScroll()

    // 5秒后恢复自动滚动
    setTimeout(() => {
      if (Date.now() - this.lastManualOperationTime >= 5000) {
        this.startAutoScroll()
      }
    }, 5000)
  }

  scrollToPrev() {
    if (this.isAnimating) return

    this.isAnimating = true
    this.scrollCount--

    // 计算上一个位置
    const prevPosition = (this.currentPosition - 1 + 6) % 6
    const prevType = prevPosition % 2 === 0 ? 'amount' : 'rate'

    // 更新排序类型
    this.currentSortType = prevType

    // 向上滚动一个grid
    this.translateYPercent = this.translateYPercent + 16.67

    // 判断是否需要重置位置（向上滚动了2次，回到相同类型）
    if (this.scrollCount <= -2 && prevType === 'amount') {
      // 动画结束后重置位置
      setTimeout(() => {
        this.isAnimating = false
        this.$nextTick(() => {
          this.translateYPercent = -33.33  // 重置到第2组的amount位置
          this.currentPosition = 2
          this.scrollCount = 0
        })
      }, 600)
    } else {
      // 正常更新位置
      this.currentPosition = prevPosition
      setTimeout(() => {
        this.isAnimating = false
      }, 600)
    }
  }

  handleWheel(event) {
    event.preventDefault()

    if (this.isAnimating) return

    // 手动操作，暂停自动滚动
    this.pauseAutoScrollTemporarily()

    if (event.deltaY > 0) {
      // 向下滚动
      this.scrollToNext()
    } else {
      // 向上滚动
      this.scrollToPrev()
    }
  }

  handleKeydown(event) {
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      // 手动操作，暂停自动滚动
      this.pauseAutoScrollTemporarily()
      this.scrollToPrev()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      // 手动操作，暂停自动滚动
      this.pauseAutoScrollTemporarily()
      this.scrollToNext()
    } else if (event.key === 'Escape' && this.isFullscreen) {
      event.preventDefault()
      this.exitFullscreen()
    }
  }

  async toggleFullscreen() {
    if (!this.isFullscreen) {
      await this.enterFullscreen()
    } else {
      await this.exitFullscreen()
    }
  }

  async enterFullscreen() {
    try {
      const element = this.$el
      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }
      this.isFullscreen = true
    } catch (error) {
      console.error('Failed to enter fullscreen:', error)
    }
  }

  async exitFullscreen() {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      }
      this.isFullscreen = false
    } catch (error) {
      console.error('Failed to exit fullscreen:', error)
    }
  }

  handleFullscreenChange() {
    // 监听浏览器全屏状态变化，同步组件状态
    this.isFullscreen = !!(
      document.fullscreenElement ||
      document.mozFullScreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    )
  }

  mounted() {
    // 添加事件监听器
    document.addEventListener('keydown', this.handleKeydown)
    // 只在grid区域监听滚轮事件
    const gridSection = this.$el.querySelector('.grid-section')
    if (gridSection) {
      gridSection.addEventListener('wheel', this.handleWheel, { passive: false })
    }

    // 添加全屏状态变化监听器
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('msfullscreenchange', this.handleFullscreenChange)

    // 启动自动滚动
    this.startAutoScroll()
  }

  beforeDestroy() {
    // 停止自动滚动
    this.stopAutoScroll()

    // 移除事件监听器
    document.removeEventListener('keydown', this.handleKeydown)
    // 移除grid区域的滚轮事件监听器
    const gridSection = this.$el.querySelector('.grid-section')
    if (gridSection) {
      gridSection.removeEventListener('wheel', this.handleWheel)
    }

    // 移除全屏状态变化监听器
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('msfullscreenchange', this.handleFullscreenChange)
  }
}
</script>

<style lang="scss" scoped>
.landscape-container {
  padding: 3vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-image: url('~@/assets/leaderboard/bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  position: relative;

  .top-left-decoration {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    pointer-events: none; // 不影响其他元素的交互
    width: 25%;

    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }

  .fullscreen-btn {
    position: absolute;
    top: 1vw;
    right: 1vw;
    width: 4vh;
    height: 4vh;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #8a5524;
    border-radius: 0.8vh;
    transition: all 0.3s ease;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 0.3vh 1vh rgba(0, 0, 0, 0.15);
    z-index: 1000;
    opacity: 1;

    &:hover {
      transform: scale(1.05);
    }

    // 全屏模式下默认隐藏，hover时显示
    &.fullscreen-mode {
      opacity: 0;

      &:hover {
        opacity: 1;
      }
    }

    i {
      font-size: 2.4vh;
    }
  }

}

.title-section {
  text-align: center;
  margin-bottom: 3vh;
  flex-shrink: 0;
  position: relative;
  display: flex;
  justify-content: center;

  .title-container {
    display: inline-flex;
    align-items: baseline;
    gap: 1vh;

    .title-text {
      font-size: 5vh;
      font-weight: 700;
      color: #8a5524;
      margin: 0;
      text-shadow: 0 0.2vh 0.4vh rgba(0, 0, 0, 0.1);
      white-space: nowrap;
    }

    .sort-indicator {
      font-size: 2.5vh;
      font-weight: 500;
      color: #8a5524;
      opacity: 0.8;
      white-space: nowrap;
      min-width: 5em; // 固定最小宽度，避免文字长度不同导致抖动
      text-align: left;
    }
  }
}

.content-container {
  flex: 1;
  display: flex;
  gap: 2vw;
  min-height: 0;
  position: relative;
  z-index: 10;
}

.ranking-section {
  width: 25%;
  min-width: 0;
}

.grid-section {
  width: 75%;
  min-width: 0;
  overflow: hidden;
  position: relative;

  .grid-wrapper {
    height: 600%; // 6个grid容器
    transition: transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1);

    &.no-transition {
      transition: none !important;
    }

    .grid-container {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr;
      gap: 2vh;
      height: 16.67%; // 100% / 6 = 16.67%

      .grid-item {
        height: 100%;
        min-height: 0;
      }
    }
  }

}
</style>
